package com.speedsoftware.nibbler.ui.settings

import androidx.compose.ui.draw.clip
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.UserRole
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.SettingsSection
import com.speedsoftware.nibbler.ui.components.ResponsiveScaffold
import com.speedsoftware.nibbler.ui.profile.ProfileSection
import com.speedsoftware.nibbler.ui.profile.SessionsSection
import com.speedsoftware.nibbler.ui.profile.TwoFactorSection
import com.speedsoftware.nibbler.ui.theme.NibblerColors
import com.speedsoftware.nibbler.ui.theme.ThemeMode
import com.speedsoftware.nibbler.ui.theme.ThemeSettings
import kotlinx.coroutines.launch

/**
 * Global settings: a section list on the left (a chip row on narrow screens) and the chosen section on the
 * right. Project-specific settings live in each project's own Settings tab.
 */
@Composable
fun SettingsScreen(appState: AppState, section: SettingsSection) {
    var selected by remember(section) { mutableStateOf(section) }

    ResponsiveScaffold(appState = appState, title = "Settings") {
        BoxWithConstraints(modifier = Modifier.fillMaxSize().background(NibblerColors.Background)) {
            val wide = maxWidth >= 760.dp
            if (wide) {
                Row(modifier = Modifier.fillMaxSize()) {
                    Column(
                        modifier = Modifier.width(240.dp).padding(start = 24.dp, top = 24.dp, end = 8.dp),
                        verticalArrangement = Arrangement.spacedBy(2.dp)
                    ) {
                        SettingsSection.values().forEach { entry ->
                            SectionItem(entry, entry == selected) { selected = entry }
                        }
                    }
                    SectionContent(appState, selected, Modifier.weight(1f))
                }
            } else {
                Column(modifier = Modifier.fillMaxSize()) {
                    SectionChips(selected) { selected = it }
                    SectionContent(appState, selected, Modifier.weight(1f))
                }
            }
        }
    }
}

@Composable
private fun SectionItem(entry: SettingsSection, isSelected: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .clickable { onClick() }
            .background(if (isSelected) NibblerColors.AccentSoft else Color.Transparent, RoundedCornerShape(8.dp))
            .padding(horizontal = 14.dp, vertical = 10.dp)
    ) {
        Text(
            entry.title,
            fontSize = 14.sp,
            fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Normal,
            color = if (isSelected) NibblerColors.Accent else NibblerColors.OnSurfaceVariant
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun SectionChips(selected: SettingsSection, onSelect: (SettingsSection) -> Unit) {
    FlowRow(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 12.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        SettingsSection.values().forEach { entry ->
            val isSelected = entry == selected
            Text(
                entry.title,
                fontSize = 13.sp,
                fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Normal,
                color = if (isSelected) NibblerColors.Accent else NibblerColors.OnSurfaceVariant,
                modifier = Modifier
                    .clip(RoundedCornerShape(20.dp))
                    .clickable { onSelect(entry) }
                    .background(if (isSelected) NibblerColors.AccentSoft else Color.Transparent, RoundedCornerShape(20.dp))
                    .border(1.dp, if (isSelected) Color.Transparent else NibblerColors.OutlineVariant, RoundedCornerShape(20.dp))
                    .padding(horizontal = 14.dp, vertical = 7.dp)
            )
        }
    }
}

@Composable
private fun SectionContent(appState: AppState, section: SettingsSection, modifier: Modifier) {
    Column(
        modifier = modifier.fillMaxWidth().verticalScroll(rememberScrollState()).padding(horizontal = 24.dp, vertical = 24.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(section.title, fontSize = 22.sp, fontWeight = FontWeight.SemiBold, color = NibblerColors.OnSurface)
            Text(section.description, fontSize = 13.sp, color = NibblerColors.OnSurfaceVariant)
        }
        when (section) {
            SettingsSection.PROFILE -> ProfileSection(appState)
            SettingsSection.SECURITY -> {
                TwoFactorSection(appState)
                SessionsSection(appState)
            }
            SettingsSection.APPEARANCE -> AppearanceSection()
            SettingsSection.ACCOUNT -> AccountSection(appState)
        }
    }
}

@Composable
private fun AppearanceSection() {
    val current = ThemeSettings.mode
    Column(
        modifier = Modifier
            .widthIn(max = 560.dp)
            .fillMaxWidth()
            .background(NibblerColors.SurfaceContainer, RoundedCornerShape(12.dp))
            .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(12.dp))
            .padding(20.dp),
        verticalArrangement = Arrangement.spacedBy(14.dp)
    ) {
        Text("Theme", fontWeight = FontWeight.SemiBold, fontSize = 15.sp, color = NibblerColors.OnSurface)
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
            ThemeCard("System", "Follows your device", ThemeMode.SYSTEM, current == ThemeMode.SYSTEM, Modifier.weight(1f).fillMaxHeight())
            ThemeCard("Dark", "Easy on the eyes", ThemeMode.DARK, current == ThemeMode.DARK, Modifier.weight(1f).fillMaxHeight())
            ThemeCard("Light", "Bright and clear", ThemeMode.LIGHT, current == ThemeMode.LIGHT, Modifier.weight(1f).fillMaxHeight())
        }
        Text(
            "Your choice is remembered.",
            fontSize = 12.sp,
            color = NibblerColors.OnSurfaceVariant
        )
    }
}

@Composable
private fun ThemeCard(title: String, subtitle: String, mode: ThemeMode, selected: Boolean, modifier: Modifier) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(10.dp))
            .clickable { ThemeSettings.select(mode) }
            .background(if (selected) NibblerColors.AccentSoft else Color.Transparent, RoundedCornerShape(10.dp))
            .border(if (selected) 2.dp else 1.dp, if (selected) NibblerColors.Accent else NibblerColors.OutlineVariant, RoundedCornerShape(10.dp))
            .padding(10.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        // Little preview of the theme: window with a sidebar, a card and an accent bar
        Row(
            modifier = Modifier.fillMaxWidth().height(54.dp).background(previewBackground(mode), RoundedCornerShape(6.dp)).padding(5.dp),
            horizontalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Box(modifier = Modifier.width(14.dp).fillMaxSize().background(previewSurface(mode), RoundedCornerShape(3.dp)))
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Box(modifier = Modifier.fillMaxWidth().height(8.dp).background(previewSurface(mode), RoundedCornerShape(3.dp)))
                Box(modifier = Modifier.width(26.dp).height(8.dp).background(Color(0xFF8B7CF6), RoundedCornerShape(3.dp)))
            }
        }
        Text(title, fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = NibblerColors.OnSurface)
        Text(subtitle, fontSize = 11.sp, color = NibblerColors.OnSurfaceVariant)
    }
}

private fun previewBackground(mode: ThemeMode): Color = when (mode) {
    ThemeMode.LIGHT -> Color(0xFFF1F1F6)
    ThemeMode.DARK -> Color(0xFF111116)
    ThemeMode.SYSTEM -> if (ThemeSettings.isDark) Color(0xFF111116) else Color(0xFFF1F1F6)
}

private fun previewSurface(mode: ThemeMode): Color = when (mode) {
    ThemeMode.LIGHT -> Color(0xFFFFFFFF)
    ThemeMode.DARK -> Color(0xFF23232D)
    ThemeMode.SYSTEM -> if (ThemeSettings.isDark) Color(0xFF23232D) else Color(0xFFFFFFFF)
}

@Composable
private fun AccountSection(appState: AppState) {
    val user = appState.currentUser ?: return
    val scope = rememberCoroutineScope()
    var confirmDelete by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    Column(
        modifier = Modifier
            .widthIn(max = 560.dp)
            .fillMaxWidth()
            .background(NibblerColors.SurfaceContainer, RoundedCornerShape(12.dp))
            .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(12.dp))
            .padding(20.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        DetailRow("Email", user.email)
        DetailRow("Role", if (user.role == UserRole.SUPER_ADMIN) "Super admin" else "Member")
        DetailRow("Server", appState.serverBaseUrl)
        Spacer(modifier = Modifier.height(4.dp))
        OutlinedButton(
            onClick = { appState.logout() },
            shape = RoundedCornerShape(8.dp),
            border = androidx.compose.foundation.BorderStroke(1.dp, NibblerColors.OutlineVariant)
        ) { Text("Log out", color = NibblerColors.OnSurface) }
    }

    // Danger zone
    Column(
        modifier = Modifier
            .widthIn(max = 560.dp)
            .fillMaxWidth()
            .border(1.dp, NibblerColors.Danger.copy(alpha = 0.5f), RoundedCornerShape(12.dp))
            .padding(20.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Text("Delete account", fontWeight = FontWeight.SemiBold, fontSize = 15.sp, color = NibblerColors.Danger)
        Text(
            "Your name, email and profile picture are removed and you are taken off every project. Issues and comments you wrote stay, shown as \"Deleted user\". This cannot be undone.",
            fontSize = 13.sp,
            color = NibblerColors.OnSurfaceVariant
        )
        error?.let { Text(it, fontSize = 12.sp, color = NibblerColors.Danger) }
        OutlinedButton(
            onClick = { error = null; confirmDelete = true },
            shape = RoundedCornerShape(8.dp),
            border = androidx.compose.foundation.BorderStroke(1.dp, NibblerColors.Danger)
        ) { Text("Delete my account", color = NibblerColors.Danger) }
    }

    if (confirmDelete) {
        AlertDialog(
            onDismissRequest = { confirmDelete = false },
            containerColor = NibblerColors.SurfaceContainerHigh,
            shape = RoundedCornerShape(12.dp),
            title = { Text("Delete your account?", color = NibblerColors.OnSurface) },
            text = { Text("This permanently removes your personal data and logs you out everywhere.", color = NibblerColors.OnSurfaceVariant) },
            confirmButton = {
                Button(
                    onClick = {
                        confirmDelete = false
                        scope.launch {
                            appState.apiClient.deleteMe()
                                .onSuccess { appState.logout() }
                                .onFailure { error = it.message ?: "Could not delete the account." }
                        }
                    },
                    shape = RoundedCornerShape(8.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.ErrorContainer, contentColor = NibblerColors.Danger)
                ) { Text("Delete") }
            },
            dismissButton = { TextButton(onClick = { confirmDelete = false }) { Text("Cancel", color = NibblerColors.OnSurfaceVariant) } }
        )
    }
}

@Composable
private fun DetailRow(label: String, value: String) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Text(label, fontSize = 13.sp, color = NibblerColors.OnSurfaceVariant, modifier = Modifier.width(100.dp))
        Text(value, fontSize = 14.sp, color = NibblerColors.OnSurface)
    }
}
